import { IMaskInputProps } from 'react-imask/dist/mixin';
import { IMask } from 'react-imask';
import { EmptyValues } from '@app/shared/constants/empty-values';
import styles from './cell-value.module.scss';
import classNames from 'classnames';
import React, { HTMLAttributes } from 'react';
import {
  abbreviatedValueFormatter,
  dateFormatter,
  numberValueFormatter,
} from '@app/shared/formatters';
import { Alignment } from '@app/shared/enums/alignment.enum';
import ExclamationMarkCircled from '@app/shared/icons/ExclamationMarkCircled';
import { GridFieldTooltipVariation } from '@app/shared/components/grid-controls/grid-field-options';
import { TextTransform } from '@app/shared/enums/text-transform.enum';
import { CellValueTheme } from '@app/shared/components/cell-value/cell-value-theme';
import { l } from '@locale/setupI18n';
import SvgTopLine from '@app/shared/icons/TopLine';

interface CellValueProps extends HTMLAttributes<HTMLElement> {
  alignment?: Alignment;
  theme?: CellValueTheme;
  textTransform?: TextTransform;
  dateFormat?: Intl.DateTimeFormatOptions;
  decimalPoint?: number;
  errorMessage?: string;
  warningMessage?: string;
  formatPattern?: IMaskInputProps;
  normalize?: boolean;
  prefix?: string;
  strong?: boolean;
  suffix?: string;
  tooltipVariation?: GridFieldTooltipVariation;
  value: string | number | boolean | undefined | null;
  dataTestId?: string;
  required?: boolean;
  isAbbreviated?: boolean;
  italics?: boolean;
  allowEmptyValues?: boolean;
  removeTrailingZeroes?: boolean;
  titleValue?: string;
  topLine?: boolean;
  plusSign?: boolean;
}

const PARENT_CLASSNAME = 'cell-value';
const CellValue = (props: CellValueProps): JSX.Element => {
  const {
    alignment,
    theme,
    textTransform,
    className,
    dateFormat,
    decimalPoint,
    errorMessage,
    warningMessage,
    formatPattern,
    normalize,
    prefix,
    strong,
    suffix,
    tooltipVariation,
    value,
    dataTestId = 'cell-value',
    required,
    isAbbreviated = false,
    italics = false,
    allowEmptyValues = false,
    removeTrailingZeroes = false,
    titleValue,
    topLine,
    plusSign,
  } = props;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const incorrectDataAttributeValue = (props as any)['data-testid'];
  if (incorrectDataAttributeValue !== undefined) {
    // eslint-disable-next-line no-console
    console.warn('Incorrect TestId declaration:', incorrectDataAttributeValue);
  }

  const numberValueFormatterValue = numberValueFormatter({
    value: typeof value == 'number' ? value : Number(value),
    fraction: decimalPoint,
    valuePrefix: prefix,
    valueSuffix: suffix,
    isNormalised: normalize,
    removeTrailingZeroes,
  });

  const numberTitleValueFormatterValue = numberValueFormatter({
    value: typeof value == 'number' ? value : Number(value),
    fraction: 6,
    valuePrefix: prefix,
    valueSuffix: suffix,
    isNormalised: normalize,
  });

  // treatZeroAsDash: controls whether 0 renders as "–" (default) or "0"
  // Set to false for title/hover values where full precision is required
  const getFormattedValue = (
    value: string | number | undefined | null,
    numberFormatter: ReturnType<typeof numberValueFormatter>,
    treatZeroAsDash = true
  ): string => {
    const hasContent = value !== null && value !== undefined && value !== '';
    const isZeroValue = value === 0 || value === '0';
    const shouldRenderDashInsteadOfZero = isZeroValue && treatZeroAsDash;

    if (!hasContent || (typeof value === 'number' && Number.isNaN(value))) {
      return '';
    }

    if (shouldRenderDashInsteadOfZero) {
      return allowEmptyValues ? '' : EmptyValues.EnDash;
    }

    const decimalPointProvided = Boolean(decimalPoint) || decimalPoint === 0;

    if (formatPattern) {
      const formattedValue = decimalPointProvided ? numberFormatter : value;
      return IMask.pipe(formattedValue.toString(), formatPattern);
    } else if (decimalPointProvided) {
      return numberFormatter;
    } else if (dateFormat) {
      return dateFormatter(value.toString(), dateFormat);
    } else {
      return `${prefix ? prefix : ''}${value.toString()}${suffix ? suffix : ''}`;
    }
  };

  const formattedValue = isAbbreviated
    ? abbreviatedValueFormatter({ value: Number(value) })
    : typeof value === 'boolean'
    ? value.toString()
    : getFormattedValue(value, numberValueFormatterValue);

  const formattedTitleValue =
    typeof value === 'boolean'
      ? value.toString()
      : getFormattedValue(value, numberTitleValueFormatterValue, false);

  const ariaLabel = topLine
    ? l('_CapHit')
    : plusSign
    ? l('_ReceivedEnhancedParticipation')
    : undefined;

  return (
    <div
      className={classNames(
        styles[PARENT_CLASSNAME],
        {
          [styles[`${PARENT_CLASSNAME}--${alignment}`]]: alignment,
          [styles[`${PARENT_CLASSNAME}--${theme}-theme`]]: theme,
          [styles[`${PARENT_CLASSNAME}--${textTransform}`]]: textTransform,
          [styles[`${PARENT_CLASSNAME}--error`]]: errorMessage,
          [styles[`${PARENT_CLASSNAME}--warning`]]: warningMessage,
          [styles[`${PARENT_CLASSNAME}--strong`]]: strong,
          [styles[`${PARENT_CLASSNAME}--plus-sign`]]: plusSign,
        },
        className
      )}
      title={titleValue ?? formattedTitleValue}
      aria-label={ariaLabel}>
      {topLine && (
        <div className={styles[`${PARENT_CLASSNAME}__top-line`]}>
          <SvgTopLine />
        </div>
      )}
      {(errorMessage || warningMessage) && (
        <ExclamationMarkCircled
          className={styles[`${PARENT_CLASSNAME}__icon`]}
          data-testid={`${dataTestId}-exclamation`}
        />
      )}
      <span
        className={classNames(styles[`${PARENT_CLASSNAME}__value`], {
          [styles[`${PARENT_CLASSNAME}__value--italics`]]: italics,
        })}
        data-testid={dataTestId}
        data-rawvalue={
          value !== undefined && value !== null
            ? suffix === '% '
              ? (Number(value) / 100).toString()
              : value.toString()
            : ''
        }>
        {formattedValue}
        {required ? <sup className={styles['required-field']}>*</sup> : ''}
      </span>
      {(errorMessage ?? warningMessage) && (
        <div
          className={classNames(styles[`${PARENT_CLASSNAME}__tooltip-content`], {
            [styles[`${PARENT_CLASSNAME}--${alignment}`]]: alignment,
            [styles[`${PARENT_CLASSNAME}__tooltip-content--${tooltipVariation}`]]: tooltipVariation,
          })}
          data-testid={`${dataTestId}-tooltip`}>
          {errorMessage ?? warningMessage}
        </div>
      )}
    </div>
  );
};

export default React.memo(CellValue);
